import { supabase } from '@/lib/supabase';

export interface PaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  roll_review_id: string;
  payment_id: string;
  tier_key: string;
  tier_label: string;
  breakdown: {
    amount_cents: number;
    platform_fee_cents: number;
    net_to_instructor_cents: number;
    currency: string;
  };
}

export interface CreatePaymentIntentInput {
  instructor_id: string;
  training_video_id: string;
  turnaround_hours?: number;
  /**
   * When true, the PaymentIntent is created with `setup_future_usage: 'off_session'`
   * so Stripe attaches the card to the Customer after the charge succeeds.
   * Off by default (opt-in on the practitioner checkout).
   */
  save_for_future?: boolean;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  roll_review_id: string;
  payment_id: string;
  breakdown: PaymentIntentResponse['breakdown'];
}

interface EdgeRequestOptions {
  ignoreReviewNotFound?: boolean;
  authErrorMessage?: string;
}

const getAccessToken = async (errorMessage = 'No auth token'): Promise<string> => {
  const token = (await supabase.auth.getSession()).data.session?.access_token;
  if (!token) throw new Error(errorMessage);
  return token;
};

const callEdgeFunction = async <T>(
  functionName: string,
  body: Record<string, unknown>,
  options: EdgeRequestOptions = {}
): Promise<T> => {
  const token = await getAccessToken(options.authErrorMessage);
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }
  );

  const payload = await response.json().catch(() => ({ error: 'Unknown error' }));

  if (!response.ok) {
    const message = payload?.error || `Failed to call ${functionName}`;
    if (options.ignoreReviewNotFound && message === 'Review not found') {
      return payload as T;
    }

    if (
      functionName === 'stripe-create-payment-intent' &&
      response.status === 400 &&
      message.includes('not completed Stripe onboarding')
    ) {
      throw new Error('Instructor has not completed Stripe onboarding');
    }

    const payloadObj = payload as { code?: unknown; error?: unknown };
    const explicitCode = typeof payloadObj.code === 'string' ? payloadObj.code : undefined;
    const inferredMachineCode =
      explicitCode ??
      (typeof payloadObj.error === 'string' && /^[A-Z][A-Z0-9_]+$/.test(payloadObj.error)
        ? payloadObj.error
        : undefined);

    throw Object.assign(new Error(message), {
      ...(inferredMachineCode ? { code: inferredMachineCode } : {}),
    });
  }

  return payload as T;
};

export const rollReviewApi = {
  createCheckoutSession(input: {
    instructorId: string;
    priceCents: number;
    currency: string;
    turnaroundHours?: number;
  }) {
    return callEdgeFunction<CheckoutSessionResponse>('stripe-create-checkout-session', {
      instructor_id:    input.instructorId,
      price_cents:      input.priceCents,
      currency:         input.currency,
      turnaround_hours: input.turnaroundHours ?? 72,
    });
  },

  createPaymentIntent(input: CreatePaymentIntentInput) {
    return callEdgeFunction<PaymentIntentResponse>(
      'stripe-create-payment-intent',
      {
        instructor_id:    input.instructor_id,
        training_video_id: input.training_video_id,
        turnaround_hours: input.turnaround_hours ?? 72,
        save_for_future:  input.save_for_future ?? false,
      },
      { authErrorMessage: 'Not authenticated' }
    );
  },

  confirmPayment(input: {
    paymentIntentId: string;
    rollReviewId: string;
    paymentId: string;
  }) {
    return callEdgeFunction('stripe-confirm-payment', {
      payment_intent_id: input.paymentIntentId,
      roll_review_id:    input.rollReviewId,
      payment_id:        input.paymentId,
    });
  },

  acceptReview(videoId: string) {
    return callEdgeFunction('roll-review-accept', {
      training_video_id: videoId,
    });
  },

  declineReview(videoId: string, practitionerId?: string) {
    return callEdgeFunction('roll-review-decline', {
      training_video_id: videoId,
      practitioner_id:   practitionerId,
    });
  },

  deliverReviewWithFeedback(input: {
    rollReviewId: string;
    feedbackText?: string;
    feedbackVideoUrl?: string;
    trainingVideoId?: string;
  }) {
    return callEdgeFunction('roll-review-deliver', {
      roll_review_id:      input.rollReviewId,
      feedback_text:       input.feedbackText,
      feedback_video_url:  input.feedbackVideoUrl,
      training_video_id:   input.trainingVideoId,
    });
  },

  async linkTrainingVideoToPaidReview(rollReviewId: string, trainingVideoId: string) {
    const { error } = await supabase
      .from('roll_reviews')
      .update({ training_video_id: trainingVideoId })
      .eq('id', rollReviewId)
      .eq('status', 'paid');
    if (error) throw new Error(error.message);
  },
};
